import {
  Breadcrumb,
  Col,
  Row,
  Typography,
  Table,
  Button,
  Form,
  Modal,
  Input,
  Switch,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { openNotification } from "../../utils";
import SettingsRouter from "./SettingsRouter";
import { NavLink } from "react-router-dom";
import ButtonGroup from "antd/es/button/button-group";
import ProspectCustomerCampaignsService from "../../services/ProspectCustomerCampaignsService";

const { Item } = Form;
const { Title } = Typography;

export const ProspectCustomerCampaigns = () => {
  const [form] = Form.useForm();
  const [formEditCampaign] = Form.useForm();
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newCampaign, setNewCampaign] = useState<string>("");
  const [newCampaignKey, setNewCampaignKey] = useState<string>("");
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
  const [processedData, setProcessedData] = useState<any[]>([]);

  const showCreateModal = () => {
    setIsCreateModalVisible(true);
  };

  const handleCreateModalCancel = () => {
    setIsCreateModalVisible(false);
  };

  const showEditModal = (campaign: any) => {
    setSelectedCampaign(campaign);
    setNewCampaign(campaign.name);
    setNewCampaignKey(campaign.campaignKey);
    setIsEditModalVisible(true);
  };

  const handleEditModalCancel = () => {
    setIsEditModalVisible(false);
    setSelectedCampaign(null);
  };

  const showDeleteModal = (campaign: any) => {
    setSelectedCampaign(campaign);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteModalCancel = () => {
    setIsDeleteModalVisible(false);
    setSelectedCampaign(null);
  };

  // POST
  const postProspectCustomerCampaign = (data: any) => {
    setLoading(true);
    ProspectCustomerCampaignsService.create(data)
      .then((response: any) => {
        getProspectCustomerCampaign();
        form.resetFields();
      })
      .catch((e: Error) => {
        console.log(e);
      })
      .finally(() => setLoading(false));
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      postProspectCustomerCampaign(values);
      setIsCreateModalVisible(false);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  // GETALL
  const getProspectCustomerCampaign = () => {
    setLoading(true);
    ProspectCustomerCampaignsService.getAll()
      .then((response: any) => {
        let data = response.data;
        setProcessedData(data);
        setLoading(false);
      })
      .catch((e: Error) => {
        console.log(e);
      });
  };

  // PUT UPDATE
  const handleChangeCampaign = (
    id: number,
    name: string,
    campaignKey: string,
    active: boolean,
  ) => {
    setLoading(true);
    let data = {
      active: active,
      name: name,
      campaignKey: campaignKey,
    };
    ProspectCustomerCampaignsService.update(id, data)
      .then((response: any) => {
        openNotification(
          "success",
          "Success",
          `Prospect Customer Campaign updated`
        );
        handleEditModalCancel();
        getProspectCustomerCampaign();
      })
      .catch((e: Error) => {
        console.log(e);
      })
      .finally(() => setLoading(false));
  };

  const handleActiveChange = (record: any, checked: boolean) => {
    handleChangeCampaign(record.id, record.name, record.campaignKey, checked);
  };

  // DELETE
  const handleDeleteCampaign = (id: number) => {
    setLoading(true);
    ProspectCustomerCampaignsService.remove(id)
      .then((response: any) => {
        openNotification(
          "success",
          "Success",
          `Prospect Customer Campaign deleted`
        );
        handleDeleteModalCancel();
        getProspectCustomerCampaign();
      })
      .catch((e: Error) => {
        console.log(e);
      })
      .finally(() => setLoading(false));
  };

  const columns: ColumnsType<any> = [
    {
      title: "Campaign Name",
      dataIndex: "name",
      key: "name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (_, row) => <span>{row.name}</span>,
    },
    {
      title: "Campaign Key",
      dataIndex: "campaignKey",
      key: "campaignKey",
      align: "center",
      sorter: (a, b) => a.campaignKey - b.campaignKey,
      render: (_, row) => <span>{row.campaignKey}</span>,
    },
    {
      title: "Active/Passive",
      dataIndex: "active",
      key: "active",
      width: 200,
      align: "center",
      sorter: (a, b) => a.active - b.active,
      render: (_, record) => (
        <Switch
          checked={record.active}
          onChange={(checked) => handleActiveChange(record, checked)}
        />
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      align: "center",
      width: 200,
      render: (_, record) => (
        <ButtonGroup style={{ marginLeft: "10px", justifyContent: "flex-end" }}>
          <Button type="primary" onClick={() => showEditModal(record)}>
            Edit
          </Button>
          <Button onClick={() => showDeleteModal(record)}>Delete</Button>
        </ButtonGroup>
      ),
    },
  ];

  useEffect(() => {
    getProspectCustomerCampaign();
  }, []);

  useEffect(() => {
    formEditCampaign.setFieldsValue({ selectedCampaign: newCampaign, selectedCampaignKey: newCampaignKey });
    // eslint-disable-next-line
  }, [newCampaign, newCampaignKey]);

  return (
    <motion.div
      initial={{ y: 200, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 300, opacity: 0 }}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 40,
      }}
    >
      <div>
        <Row>
          <Col>
            <Title className="onemaz-page-title" level={3}>
              {}
            </Title>
            <Breadcrumb
              items={[
                {
                  title: <NavLink to="/">Home</NavLink>,
                },
                {
                  title: "Settings",
                },
                {
                  title: "Call Team Customer Campaign",
                },
              ]}
            />
          </Col>
        </Row>
        <SettingsRouter />
        <Button type="primary" onClick={showCreateModal}>
          Create Call Team Customer Campaign
        </Button>
        <Row style={{ marginTop: "30px" }}>
          <Col span={24}>
            <Table
              loading={loading}
              columns={columns}
              dataSource={processedData}
              pagination={false}
              scroll={{ x: "max-content" }}
            />
          </Col>
        </Row>
        {/* Create Modal */}
        <Modal
          title="Create New Call Team Customer Campaign"
          open={isCreateModalVisible}
          onOk={handleCreate}
          onCancel={handleCreateModalCancel}
        >
          <Form form={form} layout="vertical">
            <Item
              name="name"
              label="Call Team Customer Campaign Name"
              rules={[
                {
                  required: true,
                  message: "Call Team customer campaign name required",
                },
              ]}
            >
              <Input style={{ width: "100%" }} />
            </Item>
            <Item
              name="campaignKey"
              label="Call Team Customer Campaign Key"
              rules={[
                {
                  required: true,
                  message: "Call Team customer campaign key required",
                },
              ]}
            >
              <Input style={{ width: "100%" }} />
            </Item>
          </Form>
        </Modal>
        {/* Edit Modal */}
        <Modal
          title="Edit Call Team Customer Campaign"
          open={isEditModalVisible}
          onCancel={handleEditModalCancel}
          footer={[
            <Button key="back" onClick={handleEditModalCancel}>
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={() =>
                handleChangeCampaign(
                  selectedCampaign?.id,
                  newCampaign,
                  newCampaignKey,
                  selectedCampaign?.active
                )
              }
            >
              Save
            </Button>,
          ]}
        >
          <Form form={formEditCampaign} layout="vertical">
            <Item
              name="selectedCampaign"
              label="Call Team Customer Campaign"
              rules={[
                {
                  required: true,
                  message: "Call Team customer campaign name required",
                },
              ]}
            >
              <Input
                value={newCampaign}
                onChange={(e) => setNewCampaign(e.target.value)}
              />
            </Item>
            <Item
              name="selectedCampaignKey"
              label="Call Team Customer Campaign Key"
              rules={[
                {
                  required: true,
                  message: "Call Team customer campaign key required",
                },
              ]}
            >
              <Input
                value={newCampaignKey}
                onChange={(e) => setNewCampaignKey(e.target.value)}
              />
            </Item>
          </Form>
        </Modal>
        {/* Delete Modal */}
        <Modal
          title="Delete Call Team Customer Campaign"
          open={isDeleteModalVisible}
          onCancel={handleDeleteModalCancel}
          footer={[
            <Button key="back" onClick={handleDeleteModalCancel}>
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={() => handleDeleteCampaign(selectedCampaign?.id)}
            >
              Delete
            </Button>,
          ]}
        >
          <p>Are you sure you want to delete this call team customer campaign?</p>
        </Modal>
      </div>
    </motion.div>
  );
};
